import { useCreateListing } from '../_context';
import { CreateListingSection } from './CreateListingSection';
import { CreateListingInfoSubsection } from './CreateListingInfoSubSection';
import useMultiImageUpload from '@util/hooks/useMultiImageUpload';
import { cx } from 'class-variance-authority';
import { useDropzone } from 'react-dropzone';
import AddPhotoIcon from '../_assets/AddPhotoIcon';
import Spinner from '@ui/Spinner';
import { ProductDocument } from 'models/product';
import { MediaList } from '@c/listing/components/MediaList';
import { ImageIcon } from 'i/image';
import Plus from '@c/icons/plus';

const MAX_MEDIA_QUANTITY = 10;

type UploadCallback = {
  paths: {
    thumb: string;
    full: string;
    mime_type?: string | undefined;
    download_url?: string | undefined;
  }[];
  errors: string[];
};

type UploadVideoCallback = {
  path: ProductDocument['video'] | undefined;
  errors: string[];
};

export const MediaSelect = () => {
  const { form, product } = useCreateListing();

  const errors =
    form.formState.errors.images?.message ||
    form.formState.errors.video?.message;

  const totalMediaCount = product.images.length + (product.video ? 1 : 0);

  const onUploadCallback = ({ paths, errors }: UploadCallback) => {
    const remainingSlots = MAX_MEDIA_QUANTITY - totalMediaCount;
    const newPaths = paths.slice(0, remainingSlots);
    form.setValue('images', [...product.images, ...newPaths]);

    if (errors.length) {
      form.setError('images', {
        type: 'manual',
        message: errors.join(', '),
      });
    }
  };

  const onUploadVideoCallback = ({ path, errors }: UploadVideoCallback) => {
    if (path) {
      form.setValue('video', path);
    }

    if (errors.length) {
      form.setError('video', {
        type: 'manual',
        message: errors.join(', '),
      });
    }
  };

  const {
    uploadingImage: isUploading,
    handleFileInputChange,
    uploadImages,
    deleteImage,
    uploadVideo,
  } = useMultiImageUpload({
    id: product.id,
    onUploadCallback,
    onUploadVideoCallback,
    onDeleteCallback: (paths) => {
      form.setValue('images', paths.images);
      form.setValue('video', paths.video);
    },
    type: 'listing',
    product,
  });

  const isUploadDisabled = totalMediaCount >= MAX_MEDIA_QUANTITY;

  const onDrop = async (acceptedFiles: File[]) => {
    const getImgPath = () =>
      product.seller_id + '/products/' + product.id + '/image' + Date.now();

    if (!acceptedFiles.length) return;

    const file = acceptedFiles[0];
    const isImageFile = file.type.includes('image');
    const isVideoFile = file.type.includes('video');

    if (!isImageFile && !isVideoFile) {
      form.setError('images', {
        type: 'manual',
        message: 'Only images and videos are allowed',
      });

      return;
    }

    if (isImageFile) {
      const remainingSlots = MAX_MEDIA_QUANTITY - product.images.length;
      const filesToUpload = acceptedFiles.slice(0, remainingSlots);
      const { paths, errors } = await uploadImages(filesToUpload);
      if (paths.length) {
        form.setValue('images', [...product.images, ...paths]);
      }

      if (errors.length) {
        form.setError('images', {
          type: 'manual',
          message: errors.join(', '),
        });
      }
      return;
    }

    if (isVideoFile) {
      const path = getImgPath();
      const { path: videoPath } = await uploadVideo(file, path);
      if (videoPath) {
        form.setValue('video', videoPath);
      }
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    disabled: isUploadDisabled,
    accept: {
      'image/*': [],
      'video/*': [],
    },
    multiple: true,
  });

  return (
    <CreateListingSection title="Product Photos" error={errors}>
      <div className="flex w-full flex-col justify-between sm:flex-row">
        <div className="flex flex-1 flex-col gap-4">
          <div className="flex flex-row gap-4 overflow-x-auto scrollbar-none md:flex-col">
            <div
              {...getRootProps()}
              className={cx(
                'relative flex size-[100px] shrink-0 items-center justify-center rounded-[2rem] bg-[#F6F6F6] md:hidden',
                isUploadDisabled || isUploading
                  ? 'cursor-default opacity-50'
                  : 'cursor-pointer'
              )}
            >
              <div className="absolute bottom-4 right-4 flex size-12 items-center justify-center rounded-full bg-black">
                <Plus className="text-white" />
              </div>
              <div className="flex items-center justify-center">
                {isUploading ? (
                  <Spinner className="size-12" />
                ) : (
                  <div>
                    <ImageIcon className="size-12" />
                    <input
                      {...getInputProps()}
                      onChange={handleFileInputChange}
                      type="file"
                      accept="image/*,video/*"
                      multiple
                      className="hidden"
                      disabled={isUploadDisabled || isUploading}
                    />
                  </div>
                )}
              </div>
            </div>
            <div
              {...getRootProps()}
              className={cx(
                'hidden h-[20rem] w-full items-center justify-center rounded-[1rem] border-2 border-dashed border-zinc-300 py-16 md:flex',
                isUploadDisabled || isUploading
                  ? 'cursor-default opacity-50'
                  : 'cursor-pointer'
              )}
            >
              <div className="flex w-[20rem] flex-col items-center gap-y-4 sm:w-[40rem]">
                {isUploading ? (
                  <div className="flex h-full w-full items-center justify-center">
                    <div className="flex items-center justify-center">
                      <Spinner className="!h-32 !w-32" />
                    </div>
                  </div>
                ) : (
                  <>
                    <AddPhotoIcon className="h-20 w-20 text-zinc-500" />

                    <div className="flex flex-col gap-y-2">
                      <span className="text-center text-body-sm font-semibold">
                        {isUploadDisabled
                          ? 'Maximum photos reached'
                          : isDragActive
                          ? 'Drop files here'
                          : 'Add photos or videos'}
                      </span>

                      {!isDragActive && !isUploadDisabled && (
                        <span className="text-center text-label font-medium text-text-muted">
                          or drag and drop
                        </span>
                      )}
                    </div>

                    <input
                      {...getInputProps()}
                      onChange={handleFileInputChange}
                      type="file"
                      accept="image/*,video/*"
                      multiple
                      className="hidden"
                      disabled={isUploadDisabled || isUploading}
                    />
                  </>
                )}
              </div>
            </div>

            <MediaList
              deleteMedia={deleteImage}
              onMainMediaSelect={(paths) => form.setValue('images', paths)}
              images={product.images}
              video={product.video}
            />
          </div>
          <span className="text-body-sm font-semibold text-text-muted">
            {totalMediaCount === 0
              ? 'Add up to 10 photos. Drag to reorder.'
              : `${totalMediaCount} of ${MAX_MEDIA_QUANTITY} medias`}
          </span>
        </div>
        <CreateListingInfoSubsection
          title="Set the best price for the product."
          tips={[
            'Clean and declutter the background',
            'Use natural light',
            'Experiment with angles',
          ]}
          hasCheckIcon
        />
      </div>
    </CreateListingSection>
  );
};
